import { matchPath } from 'react-router-dom';
import type { TourStep } from './tourDefinitions';

/**
 * A short tour of ONE page, listed in the Getting started checklist (when the page is in the
 * menu) and replayable from the "?" menu in the header. Unlike the app tour (one per role), page tours are per page: the
 * route guard already decides who can open a page, and any step a user can't see — a button
 * behind a permission, a panel that only renders with data — is dropped when the tour starts.
 *
 * Definitions live one file per page in ./pages and are listed in ./pages/index.ts.
 */
export interface PageTour {
  /**
   * Stored in cloud_users.tour_state, so it must match the backend's /^[a-z0-9_-]{1,64}$/i.
   * Convention: `page-<name>`, e.g. `page-alerts`.
   */
  id: string;
  /** Bump when the steps change meaningfully: everyone is offered the page tour once more. */
  version: number;
  /** The page's name as the user knows it — "Alert List". Shown in the prompt and the ? menu. */
  title: string;
  /** One line shown under the title in the Getting started checklist. */
  summary?: string;
  /** Whether this tour belongs to the current path. Build it with `route()`. */
  match: (pathname: string) => boolean;
  /**
   * 3–7 steps, each with a `target`. No intro/outro cards: the prompt is the intro, and the
   * last step's button reads "Got it".
   */
  steps: TourStep[];
}

/**
 * Match one or more react-router path patterns exactly, e.g.
 * `route('/alerts')`, `route('/nodes/:nodeId/cameras/:cameraId')`, `route('/reports/:model')`.
 */
export function route(...patterns: string[]): (pathname: string) => boolean {
  return (pathname) => patterns.some((path) => matchPath({ path, end: true }, pathname) !== null);
}

/** Selector for an element tagged `data-tour="<name>"`. */
export const tag = (name: string) => `[data-tour="${name}"]`;
